import React, { useReducer } from 'react'
import PropTypes from 'prop-types'
import HandicapHeader from './components/HandicapHeader'
import HandicapHistoryChart from './components/HandicapHistoryChart'
import ScoresSection from './components/ScoresSection'
import AddScoresForm from './components/AddScoresForm'
import PastRoundsSummary from './components/PastRoundsSummary'
import { GlgLoadingSpinner } from 'Shared/components'
import {
  buildInitialState,
  handicapHistoryReducer,
  getActiveHoles,
  getAvailableHoles,
  getChartYears,
  getHiMismatch,
  setEditedHi,
  setSelectedHoles,
  setActiveTab,
  toggleExplain,
  toggleAddForm,
  setAddForm,
} from './reducer'
import useLoadSummary from './hooks/useLoadSummary'
import useSyncHiddenField from './hooks/useSyncHiddenField'
import useSyncPlayersToRailsForm from './hooks/useSyncPlayersToRailsForm'
import { addScore, deleteScore } from './actions'
import { WhsTermsContext } from './whs_terms_context'

const t = (key, opts) => !window.I18n ? '' : window.I18n.t(key, opts)

const HandicapHistoryRoot = ({ handicapSummaryUrl, addScoresUrl, deleteScoreBaseUrl, initialHi, readOnly, courses, whsTerms }) => {
  const [state, dispatch] = useReducer(handicapHistoryReducer, initialHi, buildInitialState)
  const { loading, error, summary, editedHi, selectedHoles, activeTab, showExplain, showAddForm, saving, mutationError } = state

  useLoadSummary(handicapSummaryUrl, dispatch, t('member_handicap_history.root.failed_to_load'))
  useSyncHiddenField('member_hs_usga_index', editedHi)
  useSyncPlayersToRailsForm(summary && summary.past_rounds_summary)

  const handleAddScores = (score) =>
    addScore(dispatch, {
      url: addScoresUrl,
      score,
      failMessage: t('member_handicap_history.root.failed_to_add_scores'),
    })

  const handleDeleteScore = (scoreId) =>
    deleteScore(dispatch, {
      baseUrl: deleteScoreBaseUrl,
      scoreId,
      failMessage: t('member_handicap_history.root.failed_to_delete_score'),
    })

  // Past Rounds Summary edits always target the 18-hole index (usga_index).
  const handleUpdatePastRound = (playerId, value) => {
    const input = document.getElementById(`player_${playerId}_usga_index`)
    if (input) input.value = value
  }

  if (loading) return <div className='mhh-loading'><GlgLoadingSpinner show={true} /></div>
  if (error) return <div className='alert alert-danger'>{error}</div>
  if (!summary) return null

  const hsUnavailable = summary.hs_state === 'unavailable'

  const availableHoles = getAvailableHoles(summary)
  const activeHoles = getActiveHoles(summary, selectedHoles)
  const section = summary[activeHoles] || {}
  const years = getChartYears(section)
  const hiMismatch = getHiMismatch(summary, editedHi)

  const pastRoundsSummary = (
    <PastRoundsSummary
      rounds={summary.past_rounds_summary || []}
      onUpdate={readOnly ? null : handleUpdatePastRound}
    />
  )

  const holesSwitcher = availableHoles.length > 1 && (
    <div className='mhh-switcher'>
      {availableHoles.map(h => (
        <button key={h} type='button' onClick={() => setSelectedHoles(dispatch, h)} className={`segment-btn segment-btn--wide${activeHoles === h ? ' segment-btn--active' : ''}`}>
          {t('member_handicap_history.root.hole_label', { holes: h })}
        </button>
      ))}
    </div>
  )

  const sectionTitle = (
    <div className='mhh-title-bar'>
      <strong className='mhh-title-bar__title'>{t('member_handicap_history.root.title')}</strong>
      {holesSwitcher}
    </div>
  )

  const scoresSection = (
    <ScoresSection
      scores={section.scores || {}}
      activeTab={activeTab}
      onTabChange={tab => setActiveTab(dispatch, tab)}
      onDeleteScore={readOnly ? null : handleDeleteScore}
      scoreExplanations={section.score_explanations || {}}
      saving={saving}
      activeHoles={activeHoles}
      currentHi={section.current_hi}
      calculatedAt={section.calculated_at}
      showExplain={showExplain}
      onToggleExplain={() => toggleExplain(dispatch)}
      showAddForm={!readOnly && showAddForm}
      onToggleAddForm={readOnly ? null : () => toggleAddForm(dispatch)}
      explanation={section.explanation}
      readOnly={readOnly}
    />
  )

  return (
    <WhsTermsContext.Provider value={whsTerms}>
    <div className='member-handicap-history'>
      {!readOnly && (
        <>
          <HandicapHeader
            currentHi={editedHi}
            onHiChange={value => setEditedHi(dispatch, value)}
            hiChange={section.hi_change}
            activeCount={((section.scores || {}).active || []).length}
            validThrough={section.valid_through}
            attachedBelow={hiMismatch}
          />
          {hiMismatch && (
            <div className='mhh-hi-mismatch-banner mhh-hi-mismatch-banner--attached'>
              <i className='fa fa-exclamation-triangle mhh-hi-mismatch-banner__icon' aria-hidden='true' />
              {t('member_handicap_history.hi_mismatch')}
            </div>
          )}
          {sectionTitle}
        </>
      )}
      {mutationError && <div className='alert alert-danger'>{mutationError}</div>}
      {hsUnavailable && summary.hs_message && (
        <div className='mhh-hi-mismatch-banner' role='status'>{summary.hs_message}</div>
      )}
      {readOnly ? (
        /* Member portal: past rounds near the top, chart moved to the end */
        <>
          {pastRoundsSummary}
          {sectionTitle}
          {scoresSection}
          <HandicapHistoryChart history={section.handicap_history || []} years={years} />
        </>
      ) : (
        /* Manager portal (member profile): no chart, past rounds at the end */
        <>
          {showAddForm && (
            <AddScoresForm onSave={handleAddScores} onCancel={() => setAddForm(dispatch, false)} saving={saving} courses={courses} />
          )}
          {scoresSection}
          {pastRoundsSummary}
        </>
      )}
    </div>
    </WhsTermsContext.Provider>
  )
}

HandicapHistoryRoot.defaultProps = {
  addScoresUrl: null,
  deleteScoreBaseUrl: null,
  readOnly: false,
  courses: [],
  whsTerms: {},
}

HandicapHistoryRoot.propTypes = {
  handicapSummaryUrl: PropTypes.string.isRequired,
  addScoresUrl: PropTypes.string,
  deleteScoreBaseUrl: PropTypes.string,
  initialHi: PropTypes.string,
  readOnly: PropTypes.bool,
  courses: PropTypes.array,
  whsTerms: PropTypes.object,
}

export default HandicapHistoryRoot
